import { theme } from '@chakra-ui/theme';
import { Node, Slice } from '../api/APITypes';
import SerializedGraph from 'graphology-types';

type ProjectState = {
  id: string;
  name: string;
  refreshCounter: number;
  isAutosaveActive: boolean;
  toolOpenStatus: {
    search: boolean;
    savedCases: boolean;
    aiChat: boolean;
  };
  conversations: Conversation[];
  openConversationId: string;
  documents: {
    filter: Filter;
    refreshCounter: number;
    search: {
      type: SearchType;
      query: {
        [key in SearchType]: string;
      };
    };
    result: {
      [key in keyof DocumentResultType]?: Slice<DocumentResultType[key][]>;
    };
    structure: {
      [key in keyof DocumentStructureType]: DocumentStructureType[key];
    };
  };
};

export type Tooltype = 'quickfilter' | 'filter' | 'timeline' | 'saved_documents' | 'search';

export type GraphView = {
  id: string;
  name: string;
  centerNode?: string; // hudoc:001-12345
  serializedGraph: SerializedGraph | null;
  floatingDocuments: {
    nodeId: string;
    id: string;
  }[]; // These are the floating documents in the graph
  filter?: Filter;
};

export type SearchFields = {
  query: string;
  pagination: {
    pageNumber: number;
    pageSize: number;
  };
  filter: Partial<Filter>;
};

type Folder = {
  id: string;
  name: string;
  documents: string[];
};

type Conversation = {
  id: string;
  name: string;
  messages: Message[];
  datetime: number; // unix timestamp
};

interface Message {
  conversationId: string;
  datetime: number; // unix timestamp
  issuer: 'user' | 'system';
  content: string;
}

export type DatabaseType = 'ICJ' | 'HUDOC';

export type SearchType = 'CONTEXT' | 'EXACT' | 'GRAPH' | 'DOCUMENT' | 'FOLDER';

export type DocumentType = keyof DocumentStructureType | keyof DocumentResultType;

export type DocumentStructureType = {
  [key in keyof StructureType]: { [key: string]: StructureType[key] };
};

export type StructureType = {
  GRAPH: GraphView;
  FOLDER: Folder;
};

export type DocumentResultType = {
  CONTEXT: Node;
  EXACT: Node;
};

export enum SearchMode {
  'CONTEXT',
  'EXACT',
  'GRAPH',
  'DOCUMENT',
}

export type SortOrderType = 'default' | 'date' | 'relevance';

export default ProjectState;

export type { Folder, Message, Conversation };


// IMPORTANT
// These types are used in the backend and must be updated separately
export type Filter = {
  time: {
    upper: number;
    lower: number;
  };
  source: FilterSource
}

export type FilterSource = {
  hudoc: FilterHUDOC;
  icj: FilterICJ;
}

export type FilterHUDOC = {
  importance: filterKey[];
  conclusion: filterKey[];
  decisionLevel: filterKey[];
  countries: filterKey[];
  articles: filterKey[];
};

export type FilterICJ = {
  countries: filterKey[];
};

export type filterKey = string;

// Filters follow this structure, but are defined as constants.
// Being constants allows the label-types to infer the correct values from the label-attribute
export type filterObject = {
  [key: filterKey]: {
    label: string;
    color?: string;
    tooltip?: string;
    type?: string;
  };
};

export const Database: filterObject = {
  'hudoc': {
    label: 'European Court of Human Rights',
  },
  'icj': {
    label: 'International Court of Justice',
  },
};

export const Importance: filterObject = {
  // '0': {
  //   label: 'EurLex',
  //   color: theme.colors.green[400],
  //   tooltip: 'EurLex Cases',
  // },
  '1': {
    label: 'Key Case',
    color: theme.colors.blue[900],
    tooltip: "Key cases are judgments delivered since the inception of the new court in 1998 which have been selected and set as the official 'case-law' in the Case Reports.",
  },
  '2': {
    label: 'Level 1',
    color: theme.colors.blue[600],
    tooltip: 'Level 1 = High importance, meaning all judgements which had made a significant contribution to the development of case-law, but has not been included in the Case Reports.',
  },
  '3': {
    label: 'Level 2',
    color: theme.colors.blue[300],
    tooltip: 'Level 2 = Medium importance, meaning other judgments that, while not making a significant contribution to the case-law, they go beyond merely applying existing case law.',
  },
  '4': {
    label: 'Level 3',
    color: theme.colors.blue[100],
    tooltip: 'Level 3 = Low importance, meaning judgments of very little legal interest. This could be judgments that simply apply existing case-law, friendly settlements and strike-outs.',
  },
};

export const Conclusion: filterObject = {
  nonviolation: {
    label: 'Non-violation',
    color: theme.colors.green[400],
    tooltip: "Non-Violation = Judgments where the Court has not found a violation of the Convention or a Protocol Article.",
  },
  violation: {
    label: 'Violation',
    color: theme.colors.red[500],
    tooltip: "Violation = Judgments where the Court has found a violation of the Convention or a Protocol Article.",
  },
  // 'both': {
  //   label: 'Both',
  //   color: theme.colors.orange[400],
  //   tooltip: 'Both = Judgments where the Court has found a violation of the Convention or a Protocol Article, but also found a non-violation.',
  // },
  // 'misc': {
  //   label: 'Miscellaneous',
  //   color: theme.colors.cyan[400],
  //   tooltip: 'Miscellaneous = Judgments where there has neither been found a violation, or a non-violation. This could for example be a friendly settlement, strike-out or just satisfaction.',
  // },
};

export const DecisionLevel: filterObject = {
  COMMITTEE: {
    label: 'Committee',
  },
  CHAMBER: {
    label: 'Chamber',
  },
  GRANDCHAMBER: {
    label: 'Grand Chamber',
  },
};

export const Article: filterObject = {
  '1': {
    label: 'Article 1',
    tooltip: 'Article 1: Obligation to respect human rights',
    type: 'article',
  },
  '2': {
    label: 'Article 2',
    tooltip: 'Article 2: Right to life',
    type: 'article',
  },
  '3': {
    label: 'Article 3',
    tooltip: 'Article 3: Prohibition of torture',
    type: 'article',
  },
  '4': {
    label: 'Article 4',
    tooltip: 'Article 4: Prohibition of slavery and forced labour',
    type: 'article',
  },
  '5': {
    label: 'Article 5',
    tooltip: 'Article 5: Right to liberty and security',
    type: 'article',
  },
  '6': {
    label: 'Article 6',
    tooltip: 'Article 6: Right to a fair trial',
    type: 'article',
  },
  '7': {
    label: 'Article 7',
    tooltip: 'Article 7: No punishment without law',
    type: 'article',
  },
  '8': {
    label: 'Article 8',
    tooltip: 'Article 8: Right to respect for private and family life',
    type: 'article',
  },
  '9': {
    label: 'Article 9',
    tooltip: 'Article 9: Freedom of thought, conscience and religion',
    type: 'article',
  },
  '10': {
    label: 'Article 10',
    tooltip: 'Article 10: Freedom of expression',
    type: 'article',
  },
  '11': {
    label: 'Article 11',
    tooltip: 'Article 11: Freedom of assembly and association',
    type: 'article',
  },
  '12': {
    label: 'Article 12',
    tooltip: 'Article 12: Right to marry',
    type: 'article',
  },
  '13': {
    label: 'Article 13',
    tooltip: 'Article 13: Right to an effective remedy',
    type: 'article',
  },
  '14': {
    label: 'Article 14',
    tooltip: 'Article 14: Prohibition of discrimination',
    type: 'article',
  },
  '15': {
    label: 'Article 15',
    tooltip: 'Article 15: Derogation in time of emergency',
    type: 'article',
  },
  '16': {
    label: 'Article 16',
    tooltip: 'Article 16: Restrictions on political activity of aliens',
    type: 'article',
  },
  '17': {
    label: 'Article 17',
    tooltip: 'Article 17: Prohibition of abuse of rights',
    type: 'article',
  },
  '18': {
    label: 'Article 18',
    tooltip: 'Article 18: Limitation on use of restrictions of rights',
    type: 'article',
  },
  '1-1': {
    // label: 'Article 1 of Protocol No. 1',
    label: 'Protocol 1-1',
    tooltip: 'Article 1 of Protocol No. 1: Protection of property',
    type: 'protocol'
  },
  '1-2': {
    // label: 'Article 2 of Protocol No. 1',
    label: 'Protocol 1-2',
    tooltip: 'Article 2 of Protocol No. 1: Right to education',
    type: 'protocol'
  },
  '1-3': {
    // label: 'Article 3 of Protocol No. 1',
    label: 'Protocol 1-3',
    tooltip: 'Article 3 of Protocol No. 1: Right to free elections',
    type: 'protocol'
  },
  '4-2': {
    // label: 'Article 2 of Protocol No. 4',
    label: 'Protocol 4-2',
    tooltip: 'Article 2 of Protocol No. 4: Freedom of movement',
    type: 'protocol'
  },
  '6-1': {
    // label: 'Article 1 of Protocol No. 6',
    label: 'Protocol 6-1',
    tooltip: 'Article 1 of Protocol No. 6: Abolition of the death penalty',
    type: 'protocol'
  },
  '7-2': {
    // label: 'Article 2 of Protocol No. 7',
    label: 'Protocol 7-2',
    tooltip: 'Article 2 of Protocol No. 7: Right of appeal in criminal matters',
    type: 'protocol'
  },
  '7-3': {
    // label: 'Article 3 of Protocol No. 7',
    label: 'Protocol 7-3',
    tooltip: 'Article 3 of Protocol No. 7: Compensation for wrongful conviction',
    type: 'protocol'
  },
  '12-1': {
    // label: 'Article 1 of Protocol No. 12',
    label: 'Protocol 12-1',
    tooltip: 'Article 1 of Protocol No. 12: General prohibition of discrimination',
    type: 'protocol'
  },
};

export const CountryHUDOC: filterObject = {
  'ALB': {
    label: 'Albania',
  },
  'AND': {
    label: 'Andorra',
  },
  'ARM': {
    label: 'Armenia',
  },
  'AUT': {
    label: 'Austria',
  },
  'AZE': {
    label: 'Azerbaijan',
  },
  'BEL': {
    label: 'Belgium',
  },
  'BGR': {
    label: 'Bulgaria',
  },
  'BIH': {
    label: 'Bosnia and Herzegovina',
  },
  'CHE': {
    label: 'Switzerland',
  },
  'CYP': {
    label: 'Cyprus',
  },
  'CZE': {
    label: 'The Czech Republic',
  },
  'DEU': {
    label: 'Germany',
  },
  'DNK': {
    label: 'Denmark',
  },
  'ESP': {
    label: 'Spain',
  },
  'EST': {
    label: 'Estonia',
  },
  'FIN': {
    label: 'Finland',
  },
  'FRA': {
    label: 'France',
  },
  'GBR': {
    label: 'The United Kingdom',
  },
  'GEO': {
    label: 'Georgia',
  },
  'GRC': {
    label: 'Greece',
  },
  'HRV': {
    label: 'Croatia',
  },
  'HUN': {
    label: 'Hungary',
  },
  'IRL': {
    label: 'Ireland',
  },
  'ISL': {
    label: 'Iceland',
  },
  'ITA': {
    label: 'Italy',
  },
  'LIE': {
    label: 'Liechtenstein',
  },
  'LTU': {
    label: 'Lithuania',
  },
  'LUX': {
    label: 'Luxembourg',
  },
  'LVA': {
    label: 'Latvia',
  },
  'MDA': {
    label: 'The Republic of Moldova',
  },
  'MKD': {
    label: 'The Former Yugoslav Republic of Macedonia',
  },
  'MLT': {
    label: 'Malta',
  },
  'MNE': {
    label: 'Montenegro',
  },
  'NLD': {
    label: 'The Netherlands',
  },
  'NOR': {
    label: 'Norway',
  },
  'POL': {
    label: 'Poland',
  },
  'PRT': {
    label: 'Portugal',
  },
  'ROU': {
    label: 'Romania',
  },
  'RUS': {
    label: 'Russia',
  },
  'SMR': {
    label: 'San Marino',
  },
  'SRB': {
    label: 'Serbia',
  },
  'SVK': {
    label: 'Slovakia',
  },
  'SVN': {
    label: 'Slovenia',
  },
  'SWE': {
    label: 'Sweden',
  },
  'TUR': {
    label: 'Turkey',
  },
  'UKR': {
    label: 'Ukraine',
  }
}

export const CountryICJ: filterObject = {
  ALB: {
    label: 'Albania',
  },
  ARE: {
    label: 'United Arab Emirates',
  },
  ARG: {
    label: 'Argentina',
  },
  ARM: {
    label: 'Armenia',
  },
  AUS: {
    label: 'Australia',
  },
  AZE: {
    label: 'Azerbaijan',
  },
  BDI: {
    label: 'Burundi',
  },
  BEL: {
    label: 'Belgium',
  },
  BGR: {
    label: 'Bulgaria',
  },
  BHR: {
    label: 'Bahrain',
  },
  BOL: {
    label: 'Bolivia',
  },
  BRA: {
    label: 'Brazil',
  },
  CAN: {
    label: 'Canada',
  },
  CHE: {
    label: 'Switzerland',
  },
  CHL: {
    label: 'Chile',
  },
  COD: {
    label: 'Democratic Republic of the Congo',
  },
  COL: {
    label: 'Colombia',
  },
  CRI: {
    label: 'Costa Rica',
  },
  CSK: {
    label: 'Czechoslovakia',
  },
  DEU: {
    label: 'Germany',
  },
  DNK: {
    label: 'Denmark',
  },
  EGY: {
    label: 'Egypt',
  },
  ESP: {
    label: 'Spain',
  },
  FRA: {
    label: 'France',
  },
  'FRA-GBR-USA': {
    label:
      'France and United Kingdom of Great Britain and Northern Ireland and United States of America',
  },
  GBR: {
    label: 'United Kingdom',
  },
  GRC: {
    label: 'Greece',
  },
  GTM: {
    label: 'Guatemala',
  },
  HND: {
    label: 'Honduras',
  },
  HUN: {
    label: `Hungarian People's Republic`,
  },
  IND: {
    label: 'India',
  },
  IRN: {
    label: 'Iran',
  },
  ISL: {
    label: 'Iceland',
  },
  ITA: {
    label: 'Italy',
  },
  JPN: {
    label: 'Japan',
  },
  KEN: {
    label: 'Kenya',
  },
  LBN: {
    label: 'Lebanon',
  },
  LBY: {
    label: 'Libyan Arab Jamahiriya',
  },
  MMR: {
    label: 'Myanmar',
  },
  NGA: {
    label: 'Nigeria',
  },
  NIC: {
    label: 'Nicaragua',
  },
  NLD: {
    label: 'Netherlands',
  },
  NOR: {
    label: 'Norway',
  },
  PAK: {
    label: 'Pakistan',
  },
  PER: {
    label: 'Peru',
  },
  PRT: {
    label: 'Portugal',
  },
  QAT: {
    label: 'Qatar',
  },
  RUS: {
    label: 'Russian Federation',
  },
  RWA: {
    label: 'Rwanda',
  },
  SCG: {
    label: 'Serbia and Montenegro',
  },
  SEN: {
    label: 'Senegal',
  },
  SGP: {
    label: 'Singapore',
  },
  SRB: {
    label: 'Serbia',
  },
  SUN: {
    label: 'Union of Soviet Socialist Republics',
  },
  SWE: {
    label: 'Sweden',
  },
  THA: {
    label: 'Thailand',
  },
  TUR: {
    label: 'Turkey',
  },
  UGA: {
    label: 'Uganda',
  },
  UKR: {
    label: 'Ukraine',
  },
  URY: {
    label: 'Uruguay',
  },
  USA: {
    label: 'United States of America',
  },
  VEN: {
    label: 'Venezuela',
  },
  ZAF: {
    label: 'South Africa',
  },
};

type FiltersOf<S extends keyof FilterSource> = FilterSource[S];
export type FilterKeys<S extends keyof FilterSource> = keyof FiltersOf<S>;

export type FilterRenderType<S extends keyof FilterSource> = {
  [K in FilterKeys<S>]: {
    type: K;
    items: filterObject
    selectedItems: filterKey[];
  };
}[FilterKeys<S>];


export const FilterDefaultOptions: Filter = {
  time: {
    upper: new Date(`${new Date().getFullYear() + 1}-01-01T00:00:00.000Z`).getTime(),
    lower: new Date('1945').getTime(),
  },
  source: {
    hudoc: {
      importance: Object.keys(Importance),
      conclusion: Object.keys(Conclusion),
      decisionLevel: Object.keys(DecisionLevel),
      countries: Object.keys(CountryHUDOC),
      articles: Object.keys(Article),
    },
    icj: {
      countries: Object.keys(CountryICJ),
    }
  }
};

export function filterOption<S extends keyof FilterSource, K extends keyof FilterSource[S]>(
  source: S,
  type: K,
  filter: FilterSource,
  object: filterObject,
) {
  
  const fo = filter[source] as FilterSource[S];
  
  return {
    type: type,
    items: object,
    selectedItems: fo[type], // get filter field from type
  } as FilterRenderType<S>
  
}